import { Extension, type Editor } from '@tiptap/core';
import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { canJoin } from '@tiptap/pm/transform';

const replaceCurrentListItem = (
  editor: Editor,
  sourceListName: 'bulletList' | 'taskList',
  sourceItemName: 'listItem' | 'taskItem',
  targetListName: 'bulletList' | 'taskList',
  targetItemName: 'listItem' | 'taskItem',
  targetItemAttributes?: Record<string, unknown>
): boolean => editor.commands.command(({ state, dispatch }) => {
  const { selection, schema } = state;
  const { $from } = selection;
  const sourceListType = schema.nodes[sourceListName];
  const sourceItemType = schema.nodes[sourceItemName];
  const targetListType = schema.nodes[targetListName];
  const targetItemType = schema.nodes[targetItemName];

  if (!sourceListType || !sourceItemType || !targetListType || !targetItemType) return false;

  let itemDepth = -1;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type === sourceItemType) {
      itemDepth = depth;
      break;
    }
  }

  if (itemDepth < 1) return false;

  const listDepth = itemDepth - 1;
  const listNode = $from.node(listDepth);
  if (listNode.type !== sourceListType) return false;

  const itemNode = $from.node(itemDepth);
  const itemIndex = $from.index(listDepth);
  const listPosition = $from.before(listDepth);
  const itemPosition = $from.before(itemDepth);
  const selectionOffset = selection.from - itemPosition;
  const beforeItems: ProseMirrorNode[] = [];
  const afterItems: ProseMirrorNode[] = [];

  listNode.forEach((child, _offset, index) => {
    if (index < itemIndex) beforeItems.push(child);
    if (index > itemIndex) afterItems.push(child);
  });

  const replacementNodes: ProseMirrorNode[] = [];
  if (beforeItems.length > 0) {
    replacementNodes.push(sourceListType.create(listNode.attrs, beforeItems));
  }

  const targetItem = targetItemType.create(targetItemAttributes ?? null, itemNode.content);
  const targetList = targetListType.create(null, [targetItem]);
  const targetListPosition = listPosition + (replacementNodes[0]?.nodeSize ?? 0);
  replacementNodes.push(targetList);

  if (afterItems.length > 0) {
    replacementNodes.push(sourceListType.create(listNode.attrs, afterItems));
  }

  if (!dispatch) return true;

  const tr = state.tr.replaceWith(
    listPosition,
    listPosition + listNode.nodeSize,
    Fragment.fromArray(replacementNodes)
  );
  const targetItemPosition = targetListPosition + 1;
  const nextSelectionPosition = Math.min(
    tr.doc.content.size,
    targetItemPosition + selectionOffset
  );
  tr.setSelection(TextSelection.near(tr.doc.resolve(nextSelectionPosition)));

  const nextListBoundary = targetListPosition + targetList.nodeSize;
  if (canJoin(tr.doc, nextListBoundary)) {
    tr.join(nextListBoundary);
  }
  if (canJoin(tr.doc, targetListPosition)) {
    tr.join(targetListPosition);
  }

  dispatch(tr.scrollIntoView());
  return true;
});

const ListItemCycleShortcut = Extension.create({
  name: 'listItemCycleShortcut',
  priority: 1_000,

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': () => {
        const { editor } = this;

        if (!editor.state.selection.empty) return false;

        if (editor.isActive('taskItem')) {
          const isChecked = editor.getAttributes('taskItem').checked === true;

          if (!isChecked) {
            return editor.commands.updateAttributes('taskItem', { checked: true });
          }

          return replaceCurrentListItem(
            editor,
            'taskList',
            'taskItem',
            'bulletList',
            'listItem'
          );
        }

        if (editor.isActive('bulletList') && editor.isActive('listItem')) {
          return replaceCurrentListItem(
            editor,
            'bulletList',
            'listItem',
            'taskList',
            'taskItem',
            { checked: false }
          );
        }

        return false;
      },
    };
  },
});

export default ListItemCycleShortcut;
